import React from 'react';
import styled from 'styled-components';
import { getInitials } from '@/utils';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  src?: string;
  firstName?: string;
  lastName?: string;
  size?: AvatarSize;
  status?: 'online' | 'offline' | 'busy';
}

const sizeMap = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 80,
};

const fontSizeMap = {
  xs: '10px',
  sm: '12px',
  md: '14px',
  lg: '18px',
  xl: '24px',
};

const AvatarWrapper = styled.div<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
`;

const AvatarImage = styled.img<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  object-fit: cover;
`;

const AvatarFallback = styled.div<{ $size: number; $fontSize: string }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $fontSize }) => $fontSize};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
`;

const StatusDot = styled.div<{ $status: 'online' | 'offline' | 'busy' }>`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.surface};
  background: ${({ $status, theme }) => {
    switch ($status) {
      case 'online': return theme.colors.success;
      case 'busy': return theme.colors.warning;
      default: return theme.colors.textTertiary;
    }
  }};
`;

export const Avatar: React.FC<AvatarProps> = ({
  src,
  firstName = '',
  lastName = '',
  size = 'md',
  status
}) => {
  const pixelSize = sizeMap[size];
  const fontSize = fontSizeMap[size];

  return (
    <AvatarWrapper $size={pixelSize}>
      {src ? (
        <AvatarImage src={src} alt={`${firstName} ${lastName}`} $size={pixelSize} />
      ) : (
        <AvatarFallback $size={pixelSize} $fontSize={fontSize}>
          {getInitials(firstName, lastName)}
        </AvatarFallback>
      )}
      {status && <StatusDot $status={status} />}
    </AvatarWrapper>
  );
};

// Avatar group for displaying multiple avatars
interface AvatarGroupProps {
  children: React.ReactNode;
  max?: number;
}

const AvatarGroupWrapper = styled.div`
  display: flex;
  align-items: center;
  
  > * {
    margin-left: -8px;
    border: 2px solid ${({ theme }) => theme.colors.surface};
    border-radius: 50%;
    
    &:first-child {
      margin-left: 0;
    }
  }
`;

const MoreAvatars = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-left: -8px;
  border: 2px solid ${({ theme }) => theme.colors.surface};
`;

export const AvatarGroup: React.FC<AvatarGroupProps> = ({ children, max = 4 }) => {
  const childArray = React.Children.toArray(children);
  const visible = childArray.slice(0, max);
  const remaining = childArray.length - max;

  return (
    <AvatarGroupWrapper>
      {visible}
      {remaining > 0 && <MoreAvatars>+{remaining}</MoreAvatars>}
    </AvatarGroupWrapper>
  );
};

